import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import type { VariantProps } from "class-variance-authority"
import { CircleAlertIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { FrameVariantScope } from "@/components/ui/frame"
import { buttonVariants } from "@/components/ui/button"
import { ScopeWrapper } from "@/components/ui/scope-wrapper"
import {
  DialogNestingContext,
  dialogPushBackClasses,
  useDialogNestingRoot,
  useHasOpenChildDialog,
} from "@/components/ui/dialog-nesting"

/**
 * @example
 * import { useState } from "react"
 * import {
 *   AlertDialog,
 *   AlertDialogAction,
 *   AlertDialogCancel,
 *   AlertDialogContent,
 *   AlertDialogDescription,
 *   AlertDialogFooter,
 *   AlertDialogHeader,
 *   AlertDialogTitle,
 *   AlertDialogTrigger,
 * } from "@/components/ui/alert-dialog"
 * import { Button } from "@/components/ui/button"
 * import { TypeToConfirm } from "@/components/ui/type-to-confirm"
 *
 * export function DeleteCampaign({ name, onDelete }: { name: string; onDelete: () => void }) {
 *   const [typed, setTyped] = useState("")
 *   const confirmed = typed === name
 *   return (
 *     <AlertDialog>
 *       <AlertDialogTrigger asChild>
 *         <Button variant="destructive-ghost">Delete campaign</Button>
 *       </AlertDialogTrigger>
 *       <AlertDialogContent>
 *         <AlertDialogHeader variant="destructive">
 *           <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
 *           <AlertDialogDescription>Its creators and their emails are deleted for good.</AlertDialogDescription>
 *         </AlertDialogHeader>
 *         <TypeToConfirm phrase={name} value={typed} onValueChange={setTyped} onEnter={() => confirmed && onDelete()} />
 *         <AlertDialogFooter>
 *           <AlertDialogCancel>Cancel</AlertDialogCancel>
 *           <AlertDialogAction variant="destructive" disabled={!confirmed} onClick={onDelete}>
 *             Delete
 *           </AlertDialogAction>
 *         </AlertDialogFooter>
 *       </AlertDialogContent>
 *     </AlertDialog>
 *   )
 * }
 */
function AlertDialog({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  const { nesting, handleOpenChange } = useDialogNestingRoot({
    open,
    defaultOpen,
    onOpenChange,
  })

  return (
    <DialogNestingContext.Provider value={nesting}>
      <AlertDialogPrimitive.Root
        data-slot="alert-dialog"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogNestingContext.Provider>
  )
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  )
}

function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  )
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/25 dark:bg-black/40 backdrop-blur-xs rounded-md ease-out",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  const hasOpenChild = useHasOpenChildDialog()

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-child-open={hasOpenChild || undefined}
        // grid-cols-1: an implicit `auto` column grows to the widest no-wrap
        // child (a truncated email, a ledger row) and the dialog clips sideways.
        className={cn(
          "bg-background dark:bg-sidebar border p-5 shadow-2xl radius-full:shadow-soft-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4 fixed top-1/2 left-1/2 z-50 grid grid-cols-1 max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto duration-250 rounded-xl ease-out",
          // Nested dialogs: push this one back + up while a child dialog is open.
          dialogPushBackClasses,
          className
        )}
        {...props}
      >
        <FrameVariantScope variant="flat">{children}</FrameVariantScope>
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  )
}

/** `destructive` puts the red alert tile beside the title and description. */
function AlertDialogHeader({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & { variant?: "default" | "destructive" }) {
  const header = (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-1 text-center sm:text-left", className)}
      {...props}
    />
  )
  if (variant === "default") return header
  return (
    <div className="flex flex-col gap-2 max-sm:items-center sm:flex-row sm:gap-4">
      <div
        className="flex size-8 shrink-0 items-center justify-center rounded-pill bg-destructive/10 text-destructive"
        aria-hidden="true"
      >
        <CircleAlertIcon className="size-4 opacity-80" />
      </div>
      {header}
    </div>
  )
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-3 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("font-semibold", className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

// Footer buttons take a Button variant directly — no asChild + <Button>, no
// color classes — and always carry the scope corners, tinted like Button's.
type FooterButtonVariant = Pick<VariantProps<typeof buttonVariants>, "variant">

function scopeColor(variant: FooterButtonVariant["variant"]) {
  if (variant?.startsWith("destructive")) return "destructive"
  if (variant?.startsWith("warning")) return "warning"
  if (variant?.startsWith("success")) return "success"
  return "foreground"
}

/** The corners' wrapper; `flex *:flex-1` keeps the button full width when the footer stacks (mobile). */
function FooterScope({ variant, children }: FooterButtonVariant & { children: React.ReactNode }) {
  return (
    <ScopeWrapper color={scopeColor(variant)} className="flex *:flex-1">
      {children}
    </ScopeWrapper>
  )
}

function AlertDialogAction({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> & FooterButtonVariant) {
  return (
    <FooterScope variant={variant}>
      <AlertDialogPrimitive.Action
        className={cn(buttonVariants({ variant }), className)}
        {...props}
      />
    </FooterScope>
  )
}

function AlertDialogCancel({
  className,
  variant = "secondary",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> & FooterButtonVariant) {
  return (
    <FooterScope variant={variant}>
      <AlertDialogPrimitive.Cancel
        className={cn(buttonVariants({ variant }), className)}
        {...props}
      />
    </FooterScope>
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
